import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import {
  SyncCentresResult,
  useSyncCentres,
  useSyncCentresProgress,
} from "../../../api/appointment";
import useStyles from "./styles";

type ModalSyncCentresProps = {
  open: boolean;
  onClose?: () => void;
  /** Receives the report so the page can render it after the modal closes. */
  onResult?: (result: SyncCentresResult) => void;
};

/**
 * Confirmation before a full Euromaster centre synchronisation.
 *
 * A confirmation step rather than a bare button because this is not a read:
 * it writes across `sign`, `address` and `city` for the entire network, takes
 * minutes, and cannot be cancelled once started. The dialog exists so the
 * cost is stated before the run, not discovered during it.
 */
export const ModalSyncCentres: FC<ModalSyncCentresProps> = ({
  open,
  onClose,
  onResult,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { mutate: syncCentres, isPending } = useSyncCentres(onClose);

  // Server-side counters, read on a parallel connection while the sync POST
  // is blocked. Only polled during a run.
  const progress = useSyncCentresProgress(isPending);

  /*
   * Elapsed seconds.
   *
   * Kept alongside the server counters rather than replaced by them: the run
   * row only appears after the provider's centre list has been fetched, which
   * is itself a slow network call. For those first seconds there are no
   * counters at all, and a ticking clock is the only thing that distinguishes
   * "starting" from "hung".
   */
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!isPending) {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isPending]);

  const onConfirm = () => {
    syncCentres(undefined, {
      onSuccess: (data) => onResult?.(data),
    });
  };

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(
    elapsed % 60
  ).padStart(2, "0")}`;

  const live = progress?.state === "running" ? progress : null;
  const total = live?.total ?? 0;
  const processed = live?.processed ?? 0;

  // Only ever a determinate bar when the totals are real. A percentage
  // invented from elapsed time is a lie that costs trust the first time a
  // "90%" bar sits still for four minutes.
  const percent =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : null;

  return (
    <CustomModal
      open={open}
      title={t("centres.syncTitle", "Synchroniser les centres Euromaster")}
      // Closing mid-run must not look like cancelling: the request keeps
      // running on the server either way. Both exits are withheld while it
      // is in flight so the operator is not led to believe otherwise.
      onClose={isPending ? undefined : onClose}
      onCancel={isPending ? undefined : onClose}
      isConfirmation
      textConfirmation={
        isPending
          ? t("centres.syncRunning", "Synchronisation…")
          : t("centres.syncConfirm", "Lancer la synchronisation")
      }
      loadingConfirmation={isPending}
      onConfirm={onConfirm}
    >
      <div className={classes.textModalContainer}>
        <CustomTypography
          text={t(
            "centres.syncIntro",
            "Récupère l'intégralité du réseau Euromaster et met à jour la base :"
          )}
          variant="default"
        />

        <ul className={classes.bulletList}>
          <li>
            <Typography variant="body2">
              {t(
                "centres.syncBulletSigns",
                "création et mise à jour des centres (rapprochés par code centre)"
              )}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t(
                "centres.syncBulletAddress",
                "création des adresses et des villes manquantes"
              )}
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              {t(
                "centres.syncBulletServices",
                "rattachement des services réservables déclarés par Euromaster"
              )}
            </Typography>
          </li>
        </ul>

        {/*
          Stated up front because it is the one thing that changes what the
          operator should do: a sync that appears to hang is usually just
          slow, and the correct response is to wait, never to click again.
        */}
        <Alert severity="info">
          {t(
            "centres.syncDuration",
            "L'opération peut durer plusieurs minutes et ne peut pas être interrompue. Ne la lancez pas deux fois en parallèle : les deux exécutions écriraient sur les mêmes centres."
          )}
        </Alert>

        <Typography variant="caption" color="text.secondary">
          {t(
            "centres.syncSafety",
            "Aucun centre existant n'est supprimé. Si Euromaster ne renvoie aucun service pour un centre, ses services actuels sont conservés."
          )}
        </Typography>

        {isPending && (
          <Box sx={{ mt: 1 }}>
            <LinearProgress
              variant={percent === null ? "indeterminate" : "determinate"}
              value={percent ?? 0}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              {percent === null
                ? t("centres.syncElapsed", {
                    time: mmss,
                    defaultValue: `Synchronisation en cours — ${mmss} écoulées. Vous pouvez laisser cette fenêtre ouverte.`,
                  })
                : t("centres.syncProgress", {
                    processed,
                    total,
                    time: mmss,
                    defaultValue: `${processed}/${total} centres traités — ${mmss} écoulées.`,
                  })}
            </Typography>

            {/*
              A failure counter that climbs during the run is the earliest
              signal that something is systematically wrong (expired provider
              token, unreachable API), long before the final report exists.
            */}
            {!!live?.failed && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block" }}
              >
                {t("centres.syncLiveErrors", {
                  count: live.failed,
                  defaultValue: `${live.failed} erreur(s) jusqu'ici`,
                })}
              </Typography>
            )}

            {/*
              The server could not take its concurrency lock and chose to run
              anyway. Shown because the mitigation is human: nobody else may
              start a sync until this one ends.
            */}
            {live?.unlocked && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: "block", mt: 0.5 }}
              >
                {t(
                  "centres.syncUnlocked",
                  "Protection anti-doublon indisponible sur le serveur : ne lancez surtout pas une seconde synchronisation avant la fin de celle-ci."
                )}
              </Typography>
            )}
          </Box>
        )}
      </div>
    </CustomModal>
  );
};
